import { AsyncPipe } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ToastComponent } from '@catch/general-ui';
import { ThemingService, ToastsService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { NgSelectConfig } from '@ng-select/ng-select';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import { fadeInOutUp } from '../assets/animations/animations';
import { FooterComponent } from './components/static/footer/footer.component';
import { HeaderComponent } from './components/static/header/header.component';
import { environment } from '../environments/environment';
import { PlatformJobboardLoginModalComponent } from './platform/components/jobboard-login-modal/platform-jobboard-login-modal.component';
import { ColorService } from './services/color.service';
import { EmbedService } from './services/embed.service';
import { RefService } from './services/ref.service';
import { SEOService } from './services/seo.service';
import { isPlatformRoute } from './utils/platform-route.util';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import deLocale from 'i18n-iso-countries/langs/de.json';
import frLocale from 'i18n-iso-countries/langs/fr.json';


@UntilDestroy()
@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  animations: [fadeInOutUp],
  imports: [
    AsyncPipe,
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    ToastComponent,
    PlatformJobboardLoginModalComponent,
  ],
})
export class AppComponent implements OnInit {
  private readonly colorService = inject(ColorService);
  private readonly ngSelectConfig = inject(NgSelectConfig);
  private readonly refService = inject(RefService);
  private readonly router = inject(Router);
  private readonly seoService = inject(SEOService);
  private readonly themingService = inject(ThemingService);
  private readonly translateService = inject(TranslocoService);
  public readonly embedService = inject(EmbedService);
  public readonly toastsService = inject(ToastsService);

  platformRoute$: Observable<boolean>;

  constructor() {
    // Prefer browser path: router.url is often `/` before the first NavigationEnd.
    const initialPlatform = isPlatformRoute(
      typeof window !== 'undefined' ? window.location.pathname : this.router.url
    );
    this.platformRoute$ = this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => isPlatformRoute(this.router.url)),
      startWith(initialPlatform)
    );

    this.platformRoute$.pipe(untilDestroyed(this)).subscribe((isPlatform) => {
      if (isPlatform) {
        this.themingService.applyStoredTheme();
      } else {
        this.themingService.setLightTheme(false);
      }
    });

    this.seoService.updateKeywords(['jobbörse', 'jobsuche', 'karriere', 'bewerbung', 'stellenangebot', 'jobangebot']);
    this.colorService.setDefaultDesign();
  }

  ngOnInit(): void {
    if (!environment.debug) {
      this.translateService
        .selectTranslate(['ct-library.user-console-stop', 'ct-library.user-console-warning'])
        .pipe(untilDestroyed(this))
        .subscribe({
          next: result => {
            console.info(
              `%c${result['ct-library.user-console-stop']}`,
              'color: red; font-size: 30px; font-weight: bold;'
            );
            console.info(`%c${result['ct-library.user-console-warning']}`, 'font-size: 20px; font-weight: bold;');
          },
        });
    }

    this.translateService
      .selectTranslate('ct-library.constants.ng-select.placeholders.not-found')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: result => {
          this.ngSelectConfig.notFoundText = result;
        },
      });

    countries.registerLocale(enLocale);
    countries.registerLocale(deLocale);
    countries.registerLocale(frLocale);
  }
}
